import { Editor, EditorContent, useEditor, useEditorState } from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import { EntityType } from "../../Common.types.ts";
import { SuggestionKeyDownProps } from "@tiptap/suggestion";
import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { alpha, Box } from "@mui/material";
import { DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import { NoNewLine, ResizableImage } from "./MentionExtentions.ts";
import { inputVariant } from "../dotLegalTextFieldWithSnippets/DotLegalTextFieldWithSnippets.tsx";
import { getRelationsFromMentions, setEditorContent, useMentionHook } from "./MentionInput.hooks.ts";
import { isNullOrWhitespace } from "../../stringOperations.ts";
import * as Popper from "@popperjs/core";
import { useTranslation } from "../../../localization/useTranslation.ts";
import { Placeholder } from "@tiptap/extensions";
import { RelationViewModel } from "../../../taskManagement/createTaskPane/CreateTaskPane.types.ts";
import { createDocument, getHTMLFromFragment } from "@tiptap/core";
import { usePlanContext } from "../../../auth/planProvider/PlanProvider.tsx";
import { Table, TableRow, TableHeader, TableCell } from "@tiptap/extension-table";
import { GapCursor } from "@tiptap/pm/gapcursor";
import MentionInputToolbar, { MentionInputToolbarRef } from "./MentionInputToolbar.tsx";
import MentionInputTableControls from "./MentionInputTableControls.tsx";
import { TableCommands } from "./MentionInputTableExtension.ts";

export type textDecoration = "line-through";
export type linkColor = "primary" | "primary-dark" | "secondary" | "white";

interface BaseMentionInputProps {
    value: string | undefined;
    onChange: (value: string, relations: Array<RelationViewModel>) => void;
    rows?: number;
    maxRows?: number;
    disabled?: boolean;
    multiline?: boolean;
    label?: string;
    variant?: inputVariant;
    toolTipText?: string;
    expandToMultilineOnFocus?: boolean;
    initialTextFieldFocus?: boolean;
    manageRelationsExternal?: boolean;
    popperPlacement?: Popper.Placement;
    debounce?: boolean;
    placeholder?: string;
    noMargin?: boolean;
    alwaysShowUnderline?: boolean;
    textDecoration?: textDecoration;
    linkColor?: linkColor;
    // Reports the value on every edit instead of on blur, for a form whose Save button depends on the value:
    // with blur only, the first click on a disabled Save is spent blurring the editor.
    reportEveryChange?: boolean;
    errorText?: string;
    enableImageUpload?: boolean;
    // When the editor is mounted on demand (e.g. click-to-edit), place the caret at this offset measured
    // from the top-left of the content the user clicked. Resolved against the editor's own rect after mount,
    // so it stays accurate even if the surrounding layout shifted between the click and the editor mounting.
    focusCaretOffset?: { x: number; y: number } | null;
    // Let the editor grow with its content instead of scrolling internally. Drops max-height, overflow
    // and the reserved scrollbar gutter — the gutter otherwise shifts text left and misaligns it with a
    // static read-only render of the same content.
    disableScroll?: boolean;
    useFixedLabel?: boolean;
}

type MentionSourceNotAllowed = "mentionSource is not allowed when disableMentions is true";

export type MentionInputProps = BaseMentionInputProps &
    (
        | { disableMentions: true; mentionSource?: MentionSourceNotAllowed }
        | { disableMentions?: false | undefined; mentionSource: { entityFromId: string; entityFromType: EntityType } }
    );

export interface MentionInputRef {
    editor: any; // Reference to the TipTap Editor instance
}

export type MentionRef = {
    onKeyDown: (props: SuggestionKeyDownProps) => boolean;
};

const MentionInput = forwardRef<MentionInputRef, MentionInputProps>((props, ref) => {
    const { addonsFeatures } = usePlanContext();
    const { translateString } = useTranslation();
    const disableMentions = addonsFeatures.research || !!props.disableMentions;
    const { hasFocus, setHasFocus, collapseToolbar, AtMention, HashMention, SlashMention, toolbarFocus, setToolbarFocus } = useMentionHook(
        props,
        disableMentions
    );

    const onChange = (editor: Editor) => {
        const value = isEmpty(editor) ? "" : editor.getHTML();
        props.onChange(value, getRelationsFromMentions(editor));
    };

    const isEmpty = (editor: any) => {
        if (!editor) {
            return true;
        }

        if (!isNullOrWhitespace(editor.getText())) {
            return false;
        }

        let hasImages = false;
        editor.state.doc.descendants((node: any) => {
            if (node.type.name === "image") {
                hasImages = true;
                return false;
            }
        });

        return !hasImages;
    };

    const compareContent = (editor: Editor, propsValue: string) => {
        // Normalize both operands through the editor's own schema so we are comparing two strings
        // that have gone through the same parse + serialize pipeline. A narrower extension set
        // would drop marks like Underline / Link / Image during normalization while the live
        // editor's getHTML() preserves them, making the compare always report a difference and
        // starving the sync effect of an exit condition.
        //
        // The schema is taken off the editor rather than rebuilt from extensionManager.extensions:
        // rebuilding required hand-filtering wrapper extensions out of that flat list, which broke
        // the moment ListItem started declaring addExtensions of its own and got filtered away
        // along with the wrappers, leaving bulletList's listItem+ content expression unsatisfiable.
        //
        // createDocument parses without preserveWhitespace, so it collapses trailing/repeated
        // spaces, while the live editor was created with preserveWhitespace: "full" and getHTML()
        // keeps them. Comparing a normalized propsValue against the raw getHTML() would therefore
        // report a permanent false mismatch the moment the content ends in a space, which drives
        // the sync effect to call setContent mid-typing and swallow the just-typed space. Running
        // getHTML() through the same normalization collapses whitespace on both sides so only
        // genuine content differences trigger a resync.
        const normalize = (html: string) => getHTMLFromFragment(createDocument(html, editor.schema).content, editor.schema);

        return normalize(propsValue) !== normalize(editor.getHTML());
    };

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: {
                    levels: [1, 2, 3, 4], // Support heading levels 1, 2, 3 and 4
                },
                link: {
                    HTMLAttributes: {
                        class: "mention-link",
                    },
                },
                // The trailing paragraph this appends is produced by a ProseMirror plugin, and
                // compareContent normalizes by parsing and re-serializing, which runs no plugins.
                // The live editor would therefore always carry a node the normalized props value
                // lacks, giving a difference that never resolves and a resync on every render.
                trailingNode: false,
            }),
            Placeholder.configure({
                placeholder: props.placeholder,
            }),
            NoNewLine(props.multiline || props.expandToMultilineOnFocus),
            Table.configure({ resizable: false }),
            TableRow,
            TableHeader,
            TableCell,
            TableCommands,
            ResizableImage.configure({
                inline: false,
                allowBase64: true,
                HTMLAttributes: {
                    class: "mention-input-image",
                },
            }),
            HashMention,
            SlashMention,
            AtMention, // The last implemented suggestion, will also be the default trigger, when a suggestion is deleted
        ],
        content: props.value,
        parseOptions: {
            preserveWhitespace: "full", // This will preserve all whitespace including newlines
        },
        editable: !props.disabled,
        onUpdate: ({ editor }) => {
            if (props.reportEveryChange) {
                onChange(editor);
            }
        },
        onBlur: ({ editor }) => {
            if (compareContent(editor, props.value ?? "")) {
                onChange(editor);
                if (isEmpty(editor)) {
                    // emitUpdate is false, because we handle change separately
                    editor.commands.clearContent(false);
                }
            }

            collapseToolbar();
        },
        onFocus: ({ editor }) => {
            if (!props.disabled) {
                setHasFocus(true);
                editor.commands.focus();
            }
        },
    });

    useEffect(() => {
        if (editor && props.initialTextFieldFocus) {
            editor.commands.focus();
        }
    }, [editor, props.initialTextFieldFocus]);

    useEffect(() => {
        if (!editor || !props.focusCaretOffset) return;
        // Re-anchor the click offset to where the editor actually mounted, so a layout shift between the
        // click and mount (e.g. a sibling editor's toolbar collapsing) doesn't drag the caret off the line.
        const rect = editor.view.dom.getBoundingClientRect();
        const position = editor.view.posAtCoords({ left: rect.left + props.focusCaretOffset.x, top: rect.top + props.focusCaretOffset.y });
        if (position) editor.chain().focus().setTextSelection(position.pos).run();
        else editor.commands.focus("end");
        // Intentionally runs once per editor mount — the offset belongs to the click that mounted it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editor]);

    useEffect(() => {
        // editable is only read when the editor is first created, so a later flip of props.disabled
        // would otherwise leave the underlying ProseMirror instance editable. Keep them in sync.
        editor?.setEditable(!props.disabled);
    }, [editor, props.disabled]);

    useEffect(() => {
        // Compare via the same normalize-then-compare path that onBlur uses. A raw string compare
        // would always report a mismatch when TipTap re-serializes HTML differently than the
        // backend formatted it (e.g. attribute order, whitespace between block tags), which would
        // make this effect call setContent every render and keep firing TipTap's external store —
        // the source of the "Maximum update depth exceeded" cascade after policy import.
        if (editor && compareContent(editor, props.value ?? "")) {
            setTimeout(() => {
                setEditorContent(editor, props.value ?? "");
            });
        }
    }, [props.value, editor]);

    // v3 stopped re-rendering on every transaction, so the emptiness checks that drive the label,
    // the multiline expansion and the MUI shrink state have to subscribe to the editor explicitly.
    const editorState = useEditorState({
        editor,
        selector: ({ editor }) => ({
            isEmpty: isEmpty(editor),
        }),
    });

    useImperativeHandle(ref, () => ({
        editor, // Expose the editor instance
    }));

    if (!editor) {
        return null;
    }

    const totalFocus = hasFocus || toolbarFocus;
    const multiline = props.multiline || (props.expandToMultilineOnFocus && totalFocus) || (props.expandToMultilineOnFocus && !editorState.isEmpty);

    // An empty, unfocused field keeps its label inside the box, which is the only thing standing where a
    // placeholder would go. The two cannot share that spot, so a caller gets one or the other: the mention
    // hint is appended to the label when there is no placeholder of its own, and the placeholder is held
    // back until the label floats when there is.
    const showsOwnPlaceholder = !!props.placeholder;
    const labelSitsInsideField = !isNullOrWhitespace(props.label) && !totalFocus && editorState.isEmpty;
    let label = props.label;
    if (props.label !== undefined && !showsOwnPlaceholder && !totalFocus && editorState.isEmpty && !disableMentions) {
        label = `${props.label + "."} ${translateString("mentionTriggers")}`;
    }

    const handleMentionClick = () => {
        editor?.commands.focus();
        editor?.commands.insertContent("@");
    };

    return (
        <Box
            sx={(theme) => ({
                position: "relative",
                "& .MuiFormLabel-root": {},
                "& .tiptap p": { margin: 0 },
                "& .ProseMirror-focused": {
                    outline: "none",
                },
                "& .ProseMirror": {
                    lineHeight: "1.5", // Adjust as needed for consistency
                    minHeight: multiline ? `${(props.rows ?? 1) * 1.5}em` : "1.5em", // Set minimum height based on rows (assuming 1.5em line height)
                    // With scrolling disabled the editor grows freely — no max-height, no overflow, and no
                    // reserved scrollbar gutter (the gutter would shift text left and break alignment with the
                    // static read-only view).
                    ...(props.disableScroll
                        ? { maxHeight: "none", overflowY: "visible" }
                        : {
                              maxHeight: multiline ? `${(props.maxRows ?? 30) * 1.5}em` : "1.5em", // Prevent height from exceeding row limit
                              overflowY: multiline ? "auto" : "unset", // Add vertical scrolling if content exceeds height
                              scrollbarWidth: "thin",
                              scrollbarGutter: "stable",
                          }),
                    textDecoration: props.textDecoration,
                    // Without a bottom padding the last block's bottom margin collapses out of this box,
                    // so a trailing table's margin is not part of the editor and clicking it cannot put
                    // the caret after the table. One pixel contains the margin without adding space.
                    paddingBottom: "1px",
                },
                "& .ProseMirror p.is-editor-empty:first-child::before": {
                    color: theme.palette.text.disabled,
                    display: labelSitsInsideField ? "none" : undefined,
                    content: "attr(data-placeholder)",
                    float: "left",
                    height: 0,
                    pointerEvents: "none",
                    position: "absolute",
                },
                "& .mention-link": {
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                    textDecoration: "underline",
                    cursor: "pointer",
                },
                "& .ProseMirror img": {
                    maxWidth: "calc(100% - 4px)",
                    borderRadius: "4px",
                    margin: "8px 2px",
                },
                "& .ProseMirror img.ProseMirror-selectednode": {
                    boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
                },
                "& .ProseMirror table": {
                    borderCollapse: "collapse",
                    width: "100%",
                    margin: "12px 0",
                    tableLayout: "fixed",
                    overflow: "hidden",
                },
                "& .ProseMirror th, & .ProseMirror td": {
                    border: `1px solid ${theme.palette.divider}`,
                    padding: "4px 8px",
                    verticalAlign: "top",
                    textAlign: "left",
                    position: "relative",
                },
                "& .ProseMirror th": {
                    backgroundColor: theme.palette.action.hover,
                    fontWeight: 600,
                },
                // The gap cursor sits between blocks, e.g. after a table that ends the content. Its
                // stylesheet ships with prosemirror-gapcursor but is imported nowhere, so without these
                // rules the caret is there but invisible and the editor looks stuck inside the table.
                "& .ProseMirror-gapcursor": {
                    display: "none",
                    pointerEvents: "none",
                    position: "absolute",
                },
                "& .ProseMirror-gapcursor::after": {
                    content: '""',
                    display: "block",
                    position: "absolute",
                    top: "-2px",
                    width: "20px",
                    borderTop: `1px solid ${theme.palette.text.primary}`,
                    animation: "mentionInputGapCursorBlink 1.1s steps(2, start) infinite",
                },
                "& .ProseMirror-focused .ProseMirror-gapcursor": {
                    display: "block",
                },
                "@keyframes mentionInputGapCursorBlink": {
                    to: { visibility: "hidden" },
                },
                // prosemirror-tables marks every cell of a CellSelection (whole row/column picked from a
                // table grip) with this class; without a rule the selection would be invisible.
                "& .ProseMirror .selectedCell::after": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    backgroundColor: alpha(theme.palette.primary.main, 0.12),
                    pointerEvents: "none",
                },
            })}
        >
            <DotLegalTextField
                alwaysShowUnderline={props.alwaysShowUnderline}
                noMargin={props.noMargin}
                variant={props.variant}
                label={label ?? ""}
                placeholder={props.placeholder}
                value={props.value ?? null}
                rows={props.rows}
                maxRows={props.maxRows}
                multiline={multiline}
                disabled={props.disabled}
                expandToMultilineOnFocus={props.expandToMultilineOnFocus}
                toolTipText={props.toolTipText}
                useFixedLabel={props.useFixedLabel}
                slotProps={{
                    input: {
                        inputComponent: EditorContentWrapper, // Use the CustomEditorComponent
                        inputProps: {
                            editor, // Pass the editor to the wrapper
                            hasFocus: totalFocus,
                            disabled: props.disabled,
                            disableMentions,
                            enableImageUpload: props.enableImageUpload,
                            onMentionClick: handleMentionClick,
                            onFocusChange: (focus: boolean) => {
                                setToolbarFocus(focus);
                                if (!focus) {
                                    setHasFocus(true);
                                    editor?.commands.focus();
                                }
                            },
                        },
                    },
                    inputLabel: {
                        shrink: !editorState.isEmpty ? true : undefined,
                    },
                }}
                debounce={false}
                errorText={props.errorText}
            />
        </Box>
    );
});

MentionInput.displayName = "MentionInput";
export default MentionInput;

// Wrapper Component for TipTap functionality
/*
 * This component renders the actual `EditorContent` and ensures that all Material-UI props and styles are applied.
 */
interface EditorContentWrapperProps {
    editor: any; // TipTap editor
    hasFocus?: boolean;
    disabled?: boolean;
    disableMentions: boolean;
    enableImageUpload?: boolean;
    onMentionClick?: () => void;
    onFocusChange: (focus: boolean) => void;
    [key: string]: any; // Support Material-UI's dynamic styling props
}

const EditorContentWrapper = React.forwardRef<HTMLDivElement, EditorContentWrapperProps>(
    ({ editor, hasFocus, disabled, onMentionClick, onFocusChange, disableMentions, enableImageUpload, ...props }, ref) => {
        const toolbarRef = useRef<MentionInputToolbarRef>(null);

        if (!editor) return null;

        const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
            if (disabled) {
                return;
            }

            const target = e.target as HTMLElement;
            if (target.tagName === "IMG" && enableImageUpload) {
                e.stopPropagation();
                toolbarRef.current?.openImageMenu(target);
            } else if (!placeGapCursorAfterTrailingTable(e)) {
                editor.commands.focus();
            }
        };

        // A table that ends the content has nothing below it to click into, so ProseMirror sends the
        // caret back into the nearest cell and the mouse cannot escape the table. Clicking the strip
        // under it parks a gap cursor after the table, the position the down arrow already reaches,
        // without adding anything to the document.
        const placeGapCursorAfterTrailingTable = (e: React.MouseEvent<HTMLDivElement>) => {
            const lastNode = editor.state.doc.lastChild;
            if (lastNode?.type.name !== "table") {
                return false;
            }

            const endOfContent = editor.state.doc.content.size;
            const tableDom = editor.view.nodeDOM(endOfContent - lastNode.nodeSize);
            if (!(tableDom instanceof HTMLElement)) {
                return false;
            }

            const editorRect = editor.view.dom.getBoundingClientRect();
            if (e.clientY <= tableDom.getBoundingClientRect().bottom || e.clientY > editorRect.bottom) {
                return false;
            }

            editor.view.dispatch(editor.state.tr.setSelection(new GapCursor(editor.state.doc.resolve(endOfContent))));
            editor.view.focus();
            return true;
        };

        return (
            <Box
                {...props} // Spread MUI props like padding, margins, etc.
                ref={ref as React.RefObject<HTMLDivElement>} // Forward refs for MUI
                onClick={handleClick}
                sx={{ position: "relative", paddingBottom: hasFocus ? "48px" : 0 }}
            >
                <EditorContent editor={editor} />
                <MentionInputTableControls editor={editor} display={!!hasFocus && !disabled} onFocusChange={onFocusChange} />
                <MentionInputToolbar
                    ref={toolbarRef}
                    editor={editor}
                    onMentionClick={onMentionClick}
                    display={hasFocus}
                    onFocusChange={onFocusChange}
                    disableMentions={disableMentions}
                    enableImageUpload={enableImageUpload}
                />
            </Box>
        );
    }
);

EditorContentWrapper.displayName = "EditorContentWrapper";
